import { array, option, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { Option } from 'fp-ts/Option';
import { createSelector } from 'reselect';

import { getProp, getStringProp } from '@mews/fp';
import { DateTimeUtc, DateTimeUtcService } from '@mews/framework';
import { getEnterpriseTimezone } from '../entities/enterprises/selectors';
import { isServiceHourly } from '../entities/services/selectors';
import { makeHourlyUtcDates } from './hourlyUtils';
import { getEnterpriseAvailabilityCategoryIds, getServiceAvailabilityData } from './selectors';
import { getServiceAvailabilityHash, makeUtcDates } from './utils';

interface DateRange {
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
}

const getMissingDateRange = flow(
    array.uniq(string.Eq),
    array.sort(string.Ord),
    array.map((date) => DateTimeUtcService.parse(date)),
    (dateTimes) => {
        const first = array.head(dateTimes);
        const last = array.last(dateTimes);

        if (option.isSome(first)) {
            if (option.isSome(last)) {
                return option.some({ startUtc: first.value, endUtc: last.value });
            }
            return option.some({ startUtc: first.value, endUtc: first.value });
        }
        return option.none;
    }
);

export const getMissingAvailabilityDateRange = createSelector(
    getServiceAvailabilityData,
    getEnterpriseAvailabilityCategoryIds,
    getStringProp('serviceId'),
    getProp<DateTimeUtc>()('startUtc'),
    getProp<DateTimeUtc>()('endUtc'),
    getEnterpriseTimezone,
    isServiceHourly,
    (
        serviceAvailability,
        categoryIds,
        serviceId,
        startUtc,
        endUtc,
        timezone,
        isHourlyService = false
    ): Option<DateRange> => {
        if (timezone == null || categoryIds.length === 0) {
            return option.some({ startUtc, endUtc });
        }
        const makeDateFunc = isHourlyService ? makeHourlyUtcDates : makeUtcDates;
        const dateTimeUtcCollection = makeDateFunc({ startUtc, endUtc, includeEndUtc: true, timezone });

        return pipe(
            categoryIds,
            array.flatMap((categoryId) => {
                const serviceAvailabilityHash = getServiceAvailabilityHash({ categoryId, serviceId });
                const categoryAvailability = record.lookup(serviceAvailabilityHash, serviceAvailability);
                return pipe(
                    categoryAvailability,
                    option.map(
                        flow(
                            (availability) => availability.timeUnitsAvailability,
                            record.keys,
                            (dateTimeUtcs) => array.difference(string.Eq)(dateTimeUtcs)(dateTimeUtcCollection)
                        )
                    ),
                    option.getOrElse(() => dateTimeUtcCollection)
                );
            }),
            getMissingDateRange
        );
    }
);
